// Core React libraries import
import { useContext, useEffect, useState } from 'react';

// Core ANTD and 3rdparty libraries import
import { Button, Col, Divider, Form, Input, Popconfirm, Row, Typography } from 'antd';
import {
    CloseOutlined,
    DeleteOutlined,
    EditFilled,
    InfoCircleOutlined,
    SaveFilled,
} from '@ant-design/icons';
import { ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';

// EZ web-app utils import
import { showMessage } from 'utils/notificationUtil';
// EZ web-app APIs import
import {
    addNewDocumentFolder,
    deleteDocumentFolder,
    getDocumentFolders,
    updateDocumentFolder,
} from 'utils/ez_api/AdminAPIs';
// EZ web-app types import
import { DocFolderType } from 'types/transaction';
import { updateOnboardingStep } from 'utils/onboardingUtil';
import { HomeContext } from 'context/EZContext';
// EZ web-app components import

// EZ web-app styles and assets import

const DOC_IDS_FIXED = [1, 4];

export const DocFolderSetting: React.FC<{}> = ({}) => {
    const [form] = Form.useForm();
    const HomeCtx = useContext(HomeContext);

    const [isFetchingDocFolders, setIsFetchingDocFolders] = useState(false);
    const [docFolders, setDocFolders] = useState<DocFolderType[]>([]);

    const _fetchDocFolders = () => {
        setIsFetchingDocFolders(true);
        getDocumentFolders()
            .then(({ data }) => {
                const respData = data as DocFolderType[];
                setDocFolders(respData || []);
            })
            .finally(() => {
                setIsFetchingDocFolders(false);
            });
    };

    const columns: ProColumns<DocFolderType>[] = [
        {
            title: 'Default Name',
            dataIndex: 'name',
            key: 'name',
            width: '220px',
            editable: false,
            formItemProps: (form, config) => {
                return {
                    rules: [
                        {
                            required: true,
                        },
                    ],
                };
            },
            render: (_, record) => <Typography.Text strong>{record.folder_name}</Typography.Text>,
        },
        {
            title: 'New Name',
            dataIndex: 'new_folder_name',
            key: 'new_folder_name',
            render: (_, record) => (
                <span>{record.doc_folder_preference?.folder_name || record.folder_name}</span>
            ),
            formItemProps: (form, config) => {
                return {
                    initialValue:
                        config.entity.doc_folder_preference?.folder_name ||
                        config.entity.folder_name,
                    // rules: [{ required: true }],
                };
            },
        },
        {
            title: 'Action',
            dataIndex: '',
            width: '180px',
            key: 'x',
            valueType: 'option',
            render: (_, record, index, action) =>
                !DOC_IDS_FIXED.includes(record.folder_id) ? (
                    <span>
                        <Button
                            className="p-0"
                            type="link"
                            icon={<EditFilled />}
                            title="Edit"
                            size="small"
                            onClick={() => {
                                action?.startEditable(record.folder_id);
                            }}
                        >
                            Edit Name
                        </Button>
                        {record.folder_id !== 2 ? (
                            <>
                                <Divider type="vertical" />
                                <Popconfirm
                                    placement="left"
                                    title={
                                        <>
                                            Delete <b>{record.folder_name}</b>?
                                        </>
                                    }
                                    onConfirm={() => _deleteFolder(record)}
                                >
                                    <Button
                                        className="p-0 mr-1"
                                        type="link"
                                        icon={<DeleteOutlined />}
                                        title="Delete this list"
                                        size="small"
                                        danger
                                    ></Button>
                                </Popconfirm>
                            </>
                        ) : (
                            <></>
                        )}
                    </span>
                ) : (
                    <span></span>
                ),
        },
    ];
    const onboardingCustomDocsFolder = () => {
        // update onboarding step to completed
        updateOnboardingStep('customize_document_folder', HomeCtx.refetchOnboardingList);
    };

    const _deleteFolder = async (folder: DocFolderType) => {
        await deleteDocumentFolder(folder.folder_id);
        _fetchDocFolders();
        setTimeout(() => {
            showMessage(
                'success',
                `New Folder: ${folder.folder_name} has been successfully deleted.`
            );
        }, 100);
        onboardingCustomDocsFolder();
        return true;
    };

    const _saveNewName = (folder: DocFolderType) => {
        return updateDocumentFolder(folder.folder_id, {
            folder_name: folder.folder_name,
        }).then(() => {
            _fetchDocFolders();
            setTimeout(() => {
                showMessage(
                    'success',
                    `New name: ${folder.folder_name} has been successfully saved.`
                );
            }, 100);
            onboardingCustomDocsFolder();
            return true;
        });
    };

    const _onFinish = (value: any) => {
        addNewDocumentFolder({
            folder_name: value.folder_name,
        }).then(() => {
            _fetchDocFolders();
            form.resetFields();
            setTimeout(() => {
                showMessage(
                    'success',
                    `New Folder: ${value.folder_name} has been successfully added.`
                );
            }, 100);
            onboardingCustomDocsFolder();
        });
    };

    useEffect(() => {
        _fetchDocFolders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Row>
            <Col span={10} className="mt-3 pt-3">
                <Row gutter={[0, 12]}>
                    <Col span={24}>
                        <Form layout="inline" form={form} onFinish={_onFinish} requiredMark={false}>
                            <Form.Item
                                name="folder_name"
                                rules={[{ required: true, message: "Folder name can't be empty" }]}
                            >
                                <Input
                                    style={{ width: '280px' }}
                                    allowClear
                                    placeholder="Enter the new folder name"
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit">
                                    Add Folder
                                </Button>
                            </Form.Item>
                        </Form>
                    </Col>
                    <Col span={18} style={{ textAlign: 'left' }} className="text-help">
                        <InfoCircleOutlined /> Tip: Your documents folders will be the same in all
                        transactions. The Unassigned Documents and the Disclosure Doc folders cannot
                        be renamed nor deleted.
                    </Col>
                    <Col span={6} style={{ textAlign: 'right' }}></Col>
                </Row>
            </Col>
            <Col span={14} className="mt-3">
                <ProTable
                    rowKey="folder_id"
                    showSorterTooltip={false}
                    search={false}
                    loading={isFetchingDocFolders}
                    columns={columns}
                    pagination={{ pageSize: 100 }}
                    editable={{
                        actionRender: (_, __, defaultDom) => [defaultDom.save, defaultDom.cancel],
                        type: 'multiple',
                        onSave: async (_, newData: DocFolderType, oldData) => {
                            return _saveNewName({
                                ...newData,
                                folder_name: newData.new_folder_name,
                            });
                        },
                        saveText: (
                            <>
                                <Button className="p-0 pl-1" type="link" icon={<SaveFilled />}>
                                    Save
                                </Button>
                            </>
                        ),
                        cancelText: (
                            <>
                                <Divider type="vertical" />
                                <Button
                                    className="p-0 text-grey"
                                    type="text"
                                    title="Cancel and back"
                                    icon={<CloseOutlined />}
                                ></Button>
                            </>
                        ),
                    }}
                    dataSource={docFolders}
                    options={{
                        fullScreen: false,
                        reload: false,
                        setting: false,
                        density: false,
                    }}
                />
            </Col>
        </Row>
    );
};
